import { Input } from '@up2stay/up2promo-ui';
import type { LeadStatus } from '@up2stay/up2promo-api-client';
import type { ReactNode } from 'react';

import { LeadDetailEditHint } from '@/core/components/leads/lead-detail/lead-detail-edit-hint';
import {
  LEAD_DETAIL_TITLE_INPUT_CLASS,
  LEAD_NAME_MAX_LENGTH,
} from '@/core/components/leads/lead-detail/lead-detail-field-styles';
import { LeadStatusBadge } from '@/core/components/leads/lead-detail/lead-status-badge';

type LeadDetailNameTitleProps = {
  firstNameDraft: string;
  lastNameDraft: string;
  firstNamePlaceholder: string;
  lastNamePlaceholder: string;
  isSavingName: boolean;
  status: LeadStatus;
  statusLabel: string;
  initials: string;
  subtitle?: string | null;
  /** Replaces the default status badge when provided (e.g. LeadStatusSelect). */
  statusControl?: ReactNode;
  ownershipChip?: ReactNode;
  actions?: ReactNode;
  onFirstNameChange: (value: string) => void;
  onLastNameChange: (value: string) => void;
  onFirstNameBlur: () => void;
  onLastNameBlur: () => void;
};

/**
 * Desktop hero identity + CTAs.
 * Matches LeadDetailDesktop: flex-wrap so CTAs drop below the name instead of
 * overlapping when the content column narrows (design: identity min-width 260px).
 */
export function LeadDetailNameTitle({
  firstNameDraft,
  lastNameDraft,
  firstNamePlaceholder,
  lastNamePlaceholder,
  isSavingName,
  status,
  statusLabel,
  initials,
  subtitle,
  statusControl,
  ownershipChip,
  actions,
  onFirstNameChange,
  onLastNameChange,
  onFirstNameBlur,
  onLastNameBlur,
}: LeadDetailNameTitleProps) {
  return (
    <div className="flex flex-wrap items-center gap-4 sm:gap-[18px]">
      <div className="flex min-w-0 flex-1 basis-[min(100%,16.25rem)] items-center gap-[18px]">
        <span
          className="inline-flex size-[60px] shrink-0 items-center justify-center rounded-[18px] bg-muted text-[22px] font-bold text-foreground"
          aria-hidden
        >
          {initials}
        </span>
        <div className="min-w-0 flex-1">
          <div className="inline-flex max-w-full flex-wrap items-baseline gap-x-1.5 gap-y-1">
            <LeadDetailEditHint className="relative top-[0.15em] size-3.5 self-center" />
            <Input
              value={firstNameDraft}
              onChange={(e) => onFirstNameChange(e.target.value)}
              onBlur={onFirstNameBlur}
              disabled={isSavingName}
              size={Math.min(
                Math.max(
                  firstNameDraft.length || firstNamePlaceholder.length,
                  4,
                ),
                28,
              )}
              maxLength={LEAD_NAME_MAX_LENGTH}
              title={firstNameDraft || undefined}
              className={LEAD_DETAIL_TITLE_INPUT_CLASS}
              placeholder={firstNamePlaceholder}
              aria-label={firstNamePlaceholder}
            />
            <Input
              value={lastNameDraft}
              onChange={(e) => onLastNameChange(e.target.value)}
              onBlur={onLastNameBlur}
              disabled={isSavingName}
              size={Math.min(
                Math.max(lastNameDraft.length || lastNamePlaceholder.length, 4),
                32,
              )}
              maxLength={LEAD_NAME_MAX_LENGTH}
              title={lastNameDraft || undefined}
              className={LEAD_DETAIL_TITLE_INPUT_CLASS}
              placeholder={lastNamePlaceholder}
              aria-label={lastNamePlaceholder}
            />
          </div>
          {subtitle ? (
            <p className="mt-1 truncate pl-5 text-[13.5px] text-muted-foreground">
              {subtitle}
            </p>
          ) : null}
          <div className="mt-2.5 flex flex-wrap items-center gap-2.5 pl-5">
            {statusControl ?? (
              <LeadStatusBadge status={status} label={statusLabel} />
            )}
            {ownershipChip}
          </div>
        </div>
      </div>
      {actions ? (
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          {actions}
        </div>
      ) : null}
    </div>
  );
}
